/**
 * Request deduplication service to prevent multiple identical requests
 * from being made simultaneously. This is particularly useful for components
 * that mount multiple times or when multiple components need the same data.
 */
class RequestDeduplicationService {
  private pendingRequests: Map<string, Promise<unknown>> = new Map();
  
  /**
   * Execute a request with deduplication
   * If an identical request is already in progress, return its promise
   * Otherwise, execute the request and cache its promise until completion
   */
  async dedupe<T>(
    key: string, 
    requestFn: () => Promise<T>,
    ttl: number = 0 // Time to keep the result cached after completion (0 = no caching)
  ): Promise<T> {
    // Check if we have a pending request for this key
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending as Promise<T>;
    }
    
    // Create new request promise
    const requestPromise = requestFn()
      .then(result => {
        if (ttl > 0) {
          // Keep the result cached for TTL milliseconds
          setTimeout(() => {
            this.pendingRequests.delete(key);
          }, ttl);
        } else {
          // Remove immediately after completion
          this.pendingRequests.delete(key);
        }
        return result;
      })
      .catch(error => {
        // Always remove on error
        this.pendingRequests.delete(key);
        throw error;
      });
    
    // Store the pending promise (no logging for routine requests)
    this.pendingRequests.set(key, requestPromise);
    
    return requestPromise;
  }
  
  /**
   * Clear all pending requests
   */
  clearAll() {
    this.pendingRequests.clear();
  }
  
  /**
   * Clear a specific request
   */
  clear(key: string) {
    this.pendingRequests.delete(key);
  }
  
  /**
   * Get statistics about pending requests
   */
  getStats() {
    return {
      pendingCount: this.pendingRequests.size,
      pendingKeys: Array.from(this.pendingRequests.keys())
    };
  }
}

// Export singleton instance
export const requestDedup = new RequestDeduplicationService();